import { Injectable } from '@angular/core';
import {
  FeasibilityResponse,
  CriteriaQuota,
  FeasibilityRequest,
  FeasibilityExclusionData,
  FeasibilityRangeQuota,
} from '../../../shared/interfaces/feasibility.interface';
import { FeasibilityService } from './feasibility.service';
import {
  MultiCountryDraftData,
  Multicountry,
} from '../../../shared/interfaces/create-surveys.interface';
import { deepCopy } from '../../utils/deep-copy';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { SurveyMetaDataService } from '../../../shared/services/survey-meta-data/survey-meta-data.service';
import { Qualification } from '../../../shared/interfaces/qualification.interface';
import {
  SurveyRequest,
  SupplierResponseInterface,
  GetSupplierBlendInterface,
  Qualification as BuyerQualification,
  SupplierSpecificInfo,
} from '../../../shared/interfaces/survey.interface';
import { SupplierApiService } from '../../../shared/services/supplier-api/supplier-api.service';
import { FlexibilityPricesParams } from '../../../shared/interfaces/supplier-api.interface';
import { FeasibilityError } from '../../layout/header/feasibility/domain/feasibility-error';
import { PriceMultiplier } from '../../layout/header/feasibility/domain/price-multiplier';
import {
  CPIData,
  FeasibilityData,
  GetBuyerData,
  SurveyTopBarData,
  ILocation,
} from '../../../shared/interfaces/survey-meta-data.interface';
import { FeasibilityTotal } from '../../layout/header/feasibility/feasibility.class';
import { CpiService } from '../cpi/cpi.service';
import { MultiCountryDraftService } from '../multi-country-draft/multi-country-draft.service';
import { debounceTime, skip } from 'rxjs/operators';
import { Supplier } from '../../shared/interfaces/pricing.interface';
import { PricingService } from '../pricing/pricing.service';

@Injectable({
  providedIn: 'root',
})
export class FeasibilityDataService {
  // qualification cache to the case of select suppliers in CPI
  private _cache?: {
    surveyId: number;
    language: string;
    surveyRequest: SurveyRequest;
    qualifications: Qualification[];
    isMcSurvey: boolean;
  };
  private _eligibleSuppliers: Supplier[] = [];
  private _surveyLocation = <ILocation>{};

  constructor(
    private _surveyMetaDataService: SurveyMetaDataService,
    private _feasibilityService: FeasibilityService,
    private _supplierApiService: SupplierApiService,
    private _buyerApiService: BuyerApiService,
    private _cpiService: CpiService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _pricingService: PricingService
  ) {
    _surveyMetaDataService
      .getSuppliersSelected()
      .subscribe((suppliersSelectedData) => {
        if (!suppliersSelectedData.suppliers) {
          return;
        }

        this.setFeasibility(
          this._cache!.surveyId,
          this._cache!.language,
          this._cache!.surveyRequest,
          this._cache!.qualifications,
          this._cache?.isMcSurvey || false,
          true,
          suppliersSelectedData.suppliers
        );
      });

    this._surveyMetaDataService
      .getSurveyHeaderData()
      .pipe(skip(1), debounceTime(1000))
      .subscribe((surveyTopBarData) => {
        this.checkAndGetEligibleSuppliers(surveyTopBarData);
      });
  }

  async setFeasibility(
    surveyId: number,
    language: string,
    surveyRequest: SurveyRequest,
    qualifications: Qualification[],
    isMcSurvey: boolean,
    isActiveSurvey: boolean,
    suppliers?: SupplierResponseInterface[],
    feasibilityExclusion?: FeasibilityExclusionData
  ) {
    // Only for active survey, we are running CPI/feasibility calculations in the header,
    // and caching the same survey
    if (isActiveSurvey) {
      this._surveyMetaDataService.sendFeasibilityLoading();
      this._surveyMetaDataService.sendCPILoading();
      this._cache = {
        surveyId,
        language,
        surveyRequest,
        qualifications,
        isMcSurvey,
      };
    }
    const surveyQualifications = surveyRequest.qualifications || [];
    const surveyQuotas = surveyRequest.quotas || [];
    const { quotas = [] } = surveyRequest;
    const hasQuota = !!quotas.length;
    const { expected_loi, expected_ir, field_time, completes_required } =
      surveyRequest;
    try {
      // don't request suppliers if they are received
      let suppliersRequest = suppliers;
      if (!suppliersRequest) {
        suppliersRequest = await this._feasibilityService.getSuppliers(
          surveyId,
          expected_loi,
          expected_ir,
          language,
          field_time,
          completes_required
        );
      }

      // completes_goal update base on estimated_clicks
      suppliersRequest = suppliersRequest.map((supplier) => ({
        ...supplier,
        completes_goal:
          surveyRequest.estimated_clicks || supplier.completes_goal,
        isChecked: supplier.is_active === true && supplier.quantity > 0,
      }));

      const supplier_ids =
        this._feasibilityService.mapSupplierIds(suppliersRequest);
      const { request, defaultQuotaCriteria, quotaSet } =
        this._feasibilityService.buildFeasibilityPayload(
          qualifications,
          surveyRequest,
          surveyId,
          supplier_ids,
          feasibilityExclusion?.survey_grouping,
          feasibilityExclusion?.is_exclusion
        );
      const flexibilityParamsResponse: FlexibilityPricesParams =
        await this._supplierApiService
          .getFlexibilityPricesParams(surveyId)
          .toPromise();

      const locale = isMcSurvey ? null : surveyRequest.survey_localization;
      const supplierBlendData: GetSupplierBlendInterface[] =
        await this._buyerApiService.getSupplierBlends(locale).toPromise();

      const response: FeasibilityResponse = await this._feasibilityService
        .getFeasibility(request)
        .toPromise();
      this.updateSupplierCpi(
        suppliersRequest,
        response.pricing_multplier,
        flexibilityParamsResponse.manual_override,
        flexibilityParamsResponse.survey_price_override
      );
      response.max_feasibility =
        this._feasibilityService.calculateTotalFeasibility(response);

      // add feasibility count for each supplier
      // to show feasibility for each supplier in CPI dropdown
      suppliersRequest.forEach((supplier) => {
        supplier.suppliers.forEach((singleSup) => {
          singleSup.feasibility = this._getSupplierFeasibilityCount(
            singleSup.id,
            response
          );
        });
      });

      const buyerData = this._prepareBuyerData(
        surveyId,
        language,
        surveyRequest,
        surveyQualifications,
        surveyQuotas
      );

      // Mapping suppliers to include default blend data or supplier group data
      suppliersRequest = this._mapGroupSupplierCpis(
        suppliersRequest,
        flexibilityParamsResponse
      );

      if (isMcSurvey) {
        // Send cpi/feasibility data back to MC flyout
        const feasibility = this._calculateFeasibilityForMcFlyout(
          quotaSet,
          response
        );
        const cpi = this._calculateCpiForMcFlyout(
          response,
          suppliersRequest,
          buyerData,
          flexibilityParamsResponse,
          isActiveSurvey
        );
        this._multiCountryDraftService.setMultiCountryFeasCpiData({
          surveyId: Number(surveyId),
          feasibility,
          cpi,
          currency_symbol: response?.currency_fx?.symbol,
          priceOverride: flexibilityParamsResponse.survey_price_override,
          buyerData,
        });
      }

      // Sending data to the header components only for current survey - survey showing on page
      if (isActiveSurvey) {
        const feasibilityData = this._prepareFeasibilityData(
          defaultQuotaCriteria,
          quotaSet,
          response,
          request,
          hasQuota
        );
        const cpiData = this._prepareCpiData(
          response,
          suppliersRequest,
          buyerData,
          flexibilityParamsResponse
        );
        const supplireSpecifcData =
          this._prepareSupplierSpecificData(supplierBlendData);
        this._sendFeasibilityCpiDataIntoHeader(
          supplireSpecifcData,
          feasibilityData,
          cpiData
        );
      }
    } catch (e) {
      // stop loading before throwing error
      this._surveyMetaDataService.sendFeasibilityData({ loading: false });
      throw new FeasibilityError(e);
    }
  }

  public checkAndGetEligibleSuppliers(surveyTopBarData: SurveyTopBarData) {
    const { survey_meta_data_updated, location } = surveyTopBarData;
    const surveyLocaleChanged = this.hasSurveyLocaleChanged(
      this._surveyLocation,
      location
    );
    if (
      !this._eligibleSuppliers ||
      !this._eligibleSuppliers.length ||
      survey_meta_data_updated ||
      surveyLocaleChanged
    ) {
      this.getEligibleSuppliers(surveyTopBarData);
    }
  }

  public hasSurveyLocaleChanged(
    existingLocale: ILocation,
    incomingLocale?: ILocation
  ) {
    return (
      incomingLocale &&
      (incomingLocale.countryCode !== existingLocale.countryCode ||
        incomingLocale.languageShort !== existingLocale.languageShort)
    );
  }

  public getEligibleSuppliers(surveyTopBarData: SurveyTopBarData) {
    const {
      location,
      expected_loi,
      expected_ir,
      incl_excl,
      is_pure_price_rate_card_enable,
      surveyFieldTime,
    } = surveyTopBarData;
    if (location) {
      this._pricingService
        .getEligibleSuppliers(
          location.countryCode,
          location.languageShort,
          expected_loi.toString(),
          expected_ir.toString(),
          incl_excl.toString(),
          is_pure_price_rate_card_enable,
          surveyFieldTime.toString()
        )
        .subscribe((eligibleSuppliersResponse) => {
          this._eligibleSuppliers = eligibleSuppliersResponse.suppliers;
          this._surveyLocation = location;
          const supplierSpecificInfo = this._prepareSupplierSpecificData([]);
          this._surveyMetaDataService.sendSupplierSpecificInfo(
            supplierSpecificInfo
          );
        });
    }
  }

  private _getSupplierFeasibilityCount(
    supplierId: number,
    response: FeasibilityResponse
  ) {
    const supplierWithFeasibility = response.suppliers.find(
      (sup) => Number(sup.supplier_id) === Number(supplierId)
    );
    return supplierWithFeasibility ? supplierWithFeasibility.count : 0;
  }

  getTotalFeasbilityBeforeDraftSave(
    multiCountryDraftData: MultiCountryDraftData
  ) {
    return multiCountryDraftData.countryData.reduce(
      (totalFeas: number, countryData: Multicountry) => {
        if (countryData.checked) {
          totalFeas += countryData.feasibility || 0;
        }
        return totalFeas;
      },
      0
    );
  }

  public updateSupplierCpi(
    suppliers: SupplierResponseInterface[],
    pricingMultiplier: number,
    manualOperatorOverride: boolean,
    surveyPriceOverride: number = 0
  ) {
    const priceMultiplier = new PriceMultiplier(suppliers);
    priceMultiplier.calculate(
      pricingMultiplier,
      manualOperatorOverride,
      surveyPriceOverride
    );
  }

  private _mapGroupSupplierCpis(
    suppliers: SupplierResponseInterface[],
    flexibilityParamsResponse: FlexibilityPricesParams
  ) {
    for (const supplier of suppliers) {
      if (supplier.group_ref) {
        if (
          flexibilityParamsResponse.survey_price_override ||
          flexibilityParamsResponse.manual_override
        ) {
          continue;
        }
        supplier.cpi = this.calculateGroupedSupplierCpi(
          supplier,
          this._eligibleSuppliers
        );
      }
    }
    return suppliers;
  }

  calculateGroupedSupplierCpi(
    supplier: SupplierResponseInterface,
    eligibleSuppliers: Supplier[] = []
  ) {
    let cpiAmount = 0;
    for (const groupedSupplier of supplier.suppliers) {
      const eligibleSupplierData = eligibleSuppliers.filter(
        (eligibleSupplier) => eligibleSupplier.id === groupedSupplier.id
      );
      if (eligibleSupplierData.length) {
        cpiAmount += eligibleSupplierData[0].cpi;
      }
    }
    return Number((cpiAmount / supplier.suppliers.length).toFixed(2));
  }

  private _calculateFeasibilityForMcFlyout(
    quotaSet: Map<number, CriteriaQuota[]>,
    response: FeasibilityResponse
  ) {
    const feasibilityTotal = new FeasibilityTotal(response, quotaSet);
    return feasibilityTotal.calculate();
  }

  private _calculateCpiForMcFlyout(
    response: FeasibilityResponse,
    suppliersRequest: SupplierResponseInterface[],
    buyerData: GetBuyerData,
    flexibilityParamsResponse: FlexibilityPricesParams,
    isActiveSurvey: boolean
  ) {
    if (flexibilityParamsResponse.survey_price_override) {
      return flexibilityParamsResponse.survey_price_override;
    }
    const supplierWiseCpiData = this._cpiService.mapCpi(
      suppliersRequest,
      buyerData,
      response,
      isActiveSurvey
    );
    return this._cpiService.getTotalCpi(supplierWiseCpiData, response);
  }

  private _prepareFeasibilityData(
    defaultQuotaCriteria: any,
    quotaSet: Map<number, CriteriaQuota[]>,
    response: FeasibilityResponse,
    request: FeasibilityRequest,
    hasQuota: boolean
  ): FeasibilityData {
    return {
      defaultQuota: defaultQuotaCriteria,
      quotaSet,
      response,
      request,
      hasQuota,
      loading: false,
    };
  }

  private _prepareCpiData(
    response: FeasibilityResponse,
    suppliersRequest: SupplierResponseInterface[],
    buyerData: GetBuyerData,
    flexibilityParamsResponse: FlexibilityPricesParams
  ): CPIData {
    return {
      feasibility: response,
      suppliers: deepCopy(suppliersRequest),
      buyerData,
      totalFeasibility: response.max_feasibility,
      flexibilityPrice: flexibilityParamsResponse,
      loading: false,
    };
  }

  private _prepareSupplierSpecificData(
    supplierBlendData: GetSupplierBlendInterface[]
  ): SupplierSpecificInfo {
    return {
      eligibleSuppliers: this._eligibleSuppliers,
      supplierBlendData,
    };
  }

  private _prepareBuyerData(
    surveyId: number,
    language: string,
    surveyRequest: SurveyRequest,
    surveyQualifications: BuyerQualification[],
    surveyQuotas: FeasibilityRangeQuota[]
  ): GetBuyerData {
    const {
      expected_loi,
      expected_ir,
      field_time,
      buyer_company,
      buyer_user_id,
    } = surveyRequest;
    return {
      surveyId,
      expected_loi,
      expected_ir,
      language,
      field_time,
      buyer_company,
      buyer_user_id,
      surveyQualifications,
      surveyQuotas,
      completesRequired:
        surveyRequest.estimated_clicks || surveyRequest.completes_required,
    };
  }

  private _sendFeasibilityCpiDataIntoHeader(
    supplierSpecificInfo: SupplierSpecificInfo,
    feasibilityData: FeasibilityData,
    cpiData: CPIData
  ) {
    this._surveyMetaDataService.sendSupplierSpecificInfo(supplierSpecificInfo);
    this._surveyMetaDataService.sendFeasibilityData(feasibilityData);
    this._surveyMetaDataService.sendCPIData(cpiData);
  }
}
